import React, { useState } from 'react';
import './App.css';
import emailjs from '@emailjs/browser';

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnimDone, setMenuAnimDone] = useState(true);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [testimonialAnim, setTestimonialAnim] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', date: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statsInView, setStatsInView] = useState(false);
  const [counters, setCounters] = useState({ clients: 0, treatments: 0, experience: 0 });
  const [lightboxImage, setLightboxImage] = useState(null);
  const statsRef = React.useRef(null);

  const testimonials = [
    { text: 'Dr. Poorva Sharda is the best dermatologist I have ever met! My skin has never looked better. Her personalized approach made all the difference.', author: 'A. Sharma' },
    { text: 'Very professional and caring. The clinic is equipped with state-of-the-art technology. Highly recommend!', author: 'R. Patel' },
    { text: 'State-of-the-art treatments and incredibly friendly staff. I felt comfortable from the moment I walked in.', author: 'S. Singh' },
    { text: 'The laser therapy worked wonders for my acne scars. I can\'t thank Dr. Sharda enough for restoring my confidence!', author: 'M. Gupta' },
    { text: 'Best dermatology experience ever! Dr. Sharda is knowledgeable, patient, and truly cares about her patients.', author: 'K. Verma' },
  ];

  const clinicImages = [
    { src: '/clinic-images/clinic-1.jpg', alt: 'Clinic View 1', caption: 'Reception Area' },
    { src: '/clinic-images/clinic-2.jpg', alt: 'Clinic View 2', caption: 'Treatment Room' },
    { src: '/clinic-images/clinic-3.jpg', alt: 'Clinic View 3', caption: 'Consultation Space' },
    { src: '/clinic-images/clinic-4.jpg', alt: 'Clinic View 4', caption: 'Medical Equipment' },
    { src: '/clinic-images/clinic-5.jpg', alt: 'Clinic View 5', caption: 'Waiting Area' },
    { src: '/clinic-images/clinic-6.jpg', alt: 'Clinic View 6', caption: 'Clinic Interior' },
    { src: '/clinic-images/clinic-7.jpg', alt: 'Clinic View 7', caption: 'Clinic Facilities' },
    { src: '/clinic-images/clinic-8.jpg', alt: 'Clinic View 8', caption: 'Advanced Technology' },
  ];

  const machineImages = [
    { src: 'https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=400&q=80', alt: 'Laser Machine', name: 'Fractional CO2 Laser' },
    { src: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80', alt: 'Dermatoscope', name: 'Digital Dermatoscope' },
    { src: 'https://images.unsplash.com/photo-1520880867055-1e30d1cb001c?auto=format&fit=crop&w=400&q=80', alt: 'Cryotherapy Device', name: 'Cryotherapy Unit' },
    { src: 'https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=400&q=80', alt: 'Phototherapy Unit', name: 'LED Phototherapy' },
  ];

  const faqs = [
    { q: 'What should I bring to my first appointment?', a: 'Please bring a valid ID, insurance card (if applicable), list of current medications, and any previous medical records related to your skin condition.' },
    { q: 'Do you accept insurance?', a: 'Yes, we accept most major insurance plans. Please contact our office to verify your specific coverage.' },
    { q: 'How long is a typical appointment?', a: 'Initial consultations typically last 30-45 minutes. Follow-up appointments are usually 15-30 minutes depending on the treatment.' },
    { q: 'Are treatments painful?', a: 'Most treatments are minimally invasive with little to no discomfort. We use topical anesthetics when necessary to ensure your comfort.' },
  ];

  const handleMenuToggle = () => {
    setMenuOpen(true);
    setMenuAnimDone(false);
  };
  const handleMenuClose = () => {
    setMenuOpen(false);
    setMenuAnimDone(false);
  };

  // Animate testimonial on change
  const handleTestimonialChange = (dir) => {
    setTestimonialAnim(true);
    setTimeout(() => {
      setTestimonialIndex(prev => (prev + dir + testimonials.length) % testimonials.length);
    }, 600);
  };

  // Intersection Observer for stats section
  React.useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsInView(true);
      },
      { threshold: 0.5 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  // Animate counters when stats section is in view
  React.useEffect(() => {
    if (statsInView) {
      const targets = { clients: 5000, treatments: 10000, experience: 10 };
      const duration = 2000; // 2 seconds
      const steps = 60;
      const interval = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        setCounters({
          clients: Math.floor(targets.clients * progress),
          treatments: Math.floor(targets.treatments * progress),
          experience: Math.floor(targets.experience * progress)
        });

        if (currentStep >= steps) {
          setCounters(targets);
          clearInterval(timer);
        }
      }, interval);

      return () => clearInterval(timer);
    }
  }, [statsInView]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleBookingSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // EmailJS configuration - REPLACE THESE WITH YOUR ACTUAL VALUES
    const serviceId = 'YOUR_SERVICE_ID';
    const templateId = 'YOUR_TEMPLATE_ID';
    const publicKey = 'YOUR_PUBLIC_KEY';

    // Prepare template parameters
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      preferred_date: formData.date,
      message: formData.message,
      to_email: 'info@dermatelier.com', // Your email address
    };

    // Send email using EmailJS
    emailjs.send(serviceId, templateId, templateParams, publicKey)
      .then((response) => {
        console.log('Email sent successfully!', response.status, response.text);
        alert(`Thank you, ${formData.name}! Your appointment request has been sent. We'll contact you shortly at ${formData.email}.`);
        setShowBookingModal(false);
        setFormData({ name: '', email: '', phone: '', date: '', message: '' });
      })
      .catch((error) => {
        console.error('Failed to send email:', error);
        alert('Sorry, there was an error sending your request. Please try calling us directly at +1 (234) 567-8900.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className={`derma-app${menuOpen ? ' menu-open' : ''}`}>
      {/* Hamburger menu for mobile */}
      {!menuOpen && menuAnimDone && (
        <button className="hamburger" onClick={handleMenuToggle} aria-label="Open menu">
          <span></span>
          <span></span>
          <span></span>
        </button>
      )}
      {/* Overlay when menu is open on mobile */}
      {menuOpen && <div className="menu-overlay" onClick={handleMenuClose}></div>}
      <aside className={`side-menu${menuOpen ? ' open' : ''}`}
        onTransitionEnd={() => {
          if (!menuOpen) setMenuAnimDone(true);
        }}
      >
        {menuOpen && (
          <button className="close-menu" onClick={handleMenuClose} aria-label="Close menu">&times;</button>
        )}
        <h2>DermAtelier</h2>
        <nav>
          <ul>
            <li><a href="#about" onClick={handleMenuClose}>About Us</a></li>
            <li><a href="#doctor" onClick={handleMenuClose}>Meet Our Doctor</a></li>
            <li><a href="#gallery" onClick={handleMenuClose}>Our Clinic</a></li>
            <li><a href="#treatments" onClick={handleMenuClose}>Procedures</a></li>
            <li><a href="#disorders" onClick={handleMenuClose}>Disorders</a></li>
            <li><a href="#testimonials" onClick={handleMenuClose}>Testimonials</a></li>
            <li><a href="#machines" onClick={handleMenuClose}>Equipment</a></li>
            <li><a href="#faq" onClick={handleMenuClose}>FAQ</a></li>
            <li><a href="#contact" onClick={handleMenuClose}>Contact</a></li>
          </ul>
        </nav>
        <button className="book-now-sidebar" onClick={() => { setShowBookingModal(true); handleMenuClose(); }}>📅 Book Appointment</button>
      </aside>
      <main className="main-content">
        <section id="about" className="hero-section">
          <div className="hero-content">
            <h1>Welcome to DermAtelier</h1>
            <p className="hero-subtitle">Where Science Meets Beauty</p>
            <p className="hero-description">Your skin health is our top priority. We offer personalized dermatology and cosmetology care for all ages and skin types using cutting-edge technology and evidence-based treatments.</p>
            <button className="cta-button" onClick={() => setShowBookingModal(true)}>Book Your Consultation</button>
          </div>
        </section>
        <section id="doctor" className="doctor-section">
          <h2>Meet Our Doctor</h2>
          <div className="doctor-card">
            <img src="https://randomuser.me/api/portraits/med/women/65.jpg" alt="Dr Poorva Sharda" className="doctor-photo" />
            <div className="doctor-info">
              <h3>Dr. Poorva Sharda</h3>
              <h4>Dermatologist & Cosmetologist</h4>
              <div className="credentials">
                <span className="badge">MBBS, MD (Dermatology)</span>
                <span className="badge">Board Certified</span>
                <span className="badge">10+ Years Experience</span>
              </div>
              <p>Dr. Poorva Sharda is a board-certified dermatologist and cosmetologist dedicated to providing the highest standard of skin and aesthetic care. Her approach combines medical expertise with a passion for helping patients achieve confidence and healthy, glowing skin.</p>
              <p>Specializing in both medical and cosmetic dermatology, Dr. Sharda stays at the forefront of dermatological advances to offer her patients the latest and most effective treatments available.</p>
            </div>
          </div>
        </section>
        <section id="gallery" className="gallery-section">
          <h2>Our Clinic</h2>
          <p className="section-subtitle">Experience world-class care in our state-of-the-art facility</p>
          <div className="gallery-grid">
            {clinicImages.map((img, idx) => (
              <div key={idx} className="gallery-item" onClick={() => setLightboxImage(img)}>
                <img src={img.src} alt={img.alt} className="gallery-img" />
                <div className="gallery-overlay">{img.caption}</div>
              </div>
            ))}
          </div>
        </section>
        <section id="treatments">
          <h2>Treatments Available</h2>
          <p className="section-subtitle">Advanced dermatological procedures for comprehensive skin care</p>
          <ul className="compact-list">
            <li>Laser Hair Removal</li>
            <li>Chemical Peels</li>
            <li>Botox / Fillers / Threads</li>
            <li>Acne Scar Treatment</li>
            <li>Electrosurgery</li>
            <li>PRP / GFC / Mesotherapy / Exosomes</li>
            <li>Fractional CO2 Laser Treatment</li>
            <li>Tattoo Removal</li>
            <li>Skin Allergy Treatment</li>
            <li>Wart / Mole Removal</li>
            <li>Skin Tightening Procedures</li>
            <li>Hydrafacial / Oxygeneofacial / Medifacials</li>
            <li>Laser Toning</li>
            <li>Ear Lobe Repair</li>
            <li>Cyst Removal</li>
            <li>Hair Transplantation</li>
            <li>Microdermabrasion</li>
          </ul>
        </section>
        <section id="disorders">
          <h2>Skin Disorders We Treat (त्वचा रोग)</h2>
          <p className="section-subtitle">Comprehensive treatment for all skin conditions</p>
          <ul className="compact-list bilingual-list">
            <li>Acne <span className="hindi-inline">(मुँहासे)</span></li>
            <li>Eczema <span className="hindi-inline">(खुजलीदार चकत्ते / एक्जिमा)</span></li>
            <li>Psoriasis <span className="hindi-inline">(सोरायसिस / चमड़ी की परत उधड़ना)</span></li>
            <li>Fungal Infections <span className="hindi-inline">(फंगल इन्फेक्शन / दाद)</span></li>
            <li>Vitiligo <span className="hindi-inline">(सफेद दाग)</span></li>
            <li>Melasma <span className="hindi-inline">(चेहरे पर झाइयाँ)</span></li>
            <li>Warts <span className="hindi-inline">(मस्से)</span></li>
            <li>Scabies <span className="hindi-inline">(खाज / खुजली)</span></li>
            <li>Rosacea <span className="hindi-inline">(चेहरे की लाली / गुलाबीपन)</span></li>
            <li>Hair Fall / Alopecia <span className="hindi-inline">(बाल झड़ना / गंजापन)</span></li>
            <li>Dandruff <span className="hindi-inline">(रूसी)</span></li>
            <li>Skin Allergies <span className="hindi-inline">(त्वचा की एलर्जी)</span></li>
            <li>Hives / Urticaria <span className="hindi-inline">(पित्त / चकत्ते)</span></li>
            <li>Sunburn <span className="hindi-inline">(धूप से जलन)</span></li>
            <li>Skin Pigmentation Disorders <span className="hindi-inline">(त्वचा पर रंग के धब्बे)</span></li>
            <li>Dark Circles <span className="hindi-inline">(काले घेरे)</span></li>
            <li>Skin Cancer <span className="hindi-inline">(त्वचा का कैंसर)</span></li>
            <li>Nail Disorders <span className="hindi-inline">(नाखून संबंधी समस्याएँ)</span></li>
            <li>Hyperpigmentation <span className="hindi-inline">(काली त्वचा के धब्बे)</span></li>
            <li>Moles / Birthmarks <span className="hindi-inline">(तिल / जन्म के निशान)</span></li>
          </ul>
        </section>
        <section id="testimonials" className="testimonials-section">
          <h2>Testimonials</h2>
          <div className="testimonial-slider">
            <button onClick={() => handleTestimonialChange(-1)} aria-label="Previous Testimonial">&#8592;</button>
            <div className={`testimonial${testimonialAnim ? ' testimonial-anim' : ''}`} onAnimationEnd={() => setTestimonialAnim(false)}>
              <p>"{testimonials[testimonialIndex].text}"</p>
              <span>- {testimonials[testimonialIndex].author}</span>
            </div>
            <button onClick={() => handleTestimonialChange(1)} aria-label="Next Testimonial">&#8594;</button>
          </div>
        </section>
        <section id="stats" className="stats-section" ref={statsRef}>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{counters.clients.toLocaleString()}+</div>
              <div className="stat-label">Happy Clients</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{counters.treatments.toLocaleString()}+</div>
              <div className="stat-label">Treatments Completed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{counters.experience}+</div>
              <div className="stat-label">Years of Experience</div>
            </div>
          </div>
        </section>
        <section id="machines" className="machines-section">
          <h2>Advanced Equipment</h2>
          <p className="section-subtitle">State-of-the-art technology for superior results</p>
          <div className="machines-gallery">
            {machineImages.map((img, idx) => (
              <div key={idx} className="machine-card">
                <img src={img.src} alt={img.alt} className="machine-img" />
                <div className="machine-name">{img.name}</div>
              </div>
            ))}
          </div>
        </section>
        <section id="faq" className="faq-section">
          <h2>❓ Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, idx) => (
              <details key={idx} className="faq-item">
                <summary>{faq.q}</summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
        </section>
        <section id="contact" className="contact-section">
          <h2>📍 Contact Us</h2>
          <div className="contact-grid">
            <div className="contact-info">
              <div className="contact-item">
                <div className="contact-icon">📧</div>
                <div>
                  <strong>Email</strong>
                  <p>info@dermatelier.com</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📍</div>
                <div>
                  <strong>Address</strong>
                  <p>123 Healthy Skin Avenue<br/>Medical Plaza, Floor 3<br/>City, Country 12345</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">📞</div>
                <div>
                  <strong>Phone</strong>
                  <p>+1 (234) 567-8900</p>
                </div>
              </div>
              <div className="contact-item">
                <div className="contact-icon">🕒</div>
                <div>
                  <strong>Hours</strong>
                  <p>Mon - Fri: 9:00 AM - 6:00 PM<br/>Sat: 10:00 AM - 4:00 PM<br/>Sun: Closed</p>
                </div>
              </div>
            </div>
            <div className="map-placeholder">
              <iframe 
                title="Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.2175634296703!2d-73.98823492346536!3d40.74844097138737!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259a9b3117469%3A0xd134e199a405a163!2sEmpire%20State%20Building!5e0!3m2!1sen!2sus!4v1701234567890!5m2!1sen!2sus"
                width="100%" 
                height="300" 
                style={{ border: 0, borderRadius: '12px' }} 
                allowFullScreen="" 
                loading="lazy"
              />
            </div>
          </div>
        </section>
        <footer className="footer">
          <div className="footer-content">
            <div className="footer-section">
              <h3>DermAtelier</h3>
              <p>Excellence in Dermatology & Cosmetology</p>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <a href="#about">About</a>
              <a href="#treatments">Treatments</a>
              <a href="#contact">Contact</a>
            </div>
            <div className="footer-section">
              <h4>Connect</h4>
              <div className="social-links">
                <a href="#facebook" aria-label="Facebook">📘</a>
                <a href="#instagram" aria-label="Instagram">📷</a>
                <a href="#twitter" aria-label="Twitter">🐦</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 DermAtelier. All rights reserved.</p>
          </div>
        </footer>
      </main>
      <a className="call-button" href="tel:+1234567890" title="Call Now">
        📞 Call
      </a>
      {showBookingModal && (
        <div className="modal-overlay" onClick={() => setShowBookingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowBookingModal(false)}>&times;</button>
            <h2>📅 Book an Appointment</h2>
            <form onSubmit={handleBookingSubmit} className="booking-form">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleInputChange} 
                  required 
                  placeholder="Enter your full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleInputChange} 
                  required 
                  placeholder="your@email.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleInputChange} 
                  required 
                  placeholder="+1 (234) 567-8900"
                />
              </div>
              <div className="form-group">
                <label htmlFor="date">Preferred Date *</label>
                <input 
                  type="date" 
                  id="date" 
                  name="date" 
                  value={formData.date} 
                  onChange={handleInputChange} 
                  required 
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message (Optional)</label>
                <textarea 
                  id="message" 
                  name="message" 
                  value={formData.message} 
                  onChange={handleInputChange} 
                  placeholder="Tell us about your concerns or questions..."
                  rows="4"
                />
              </div>
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Request Appointment'}
              </button>
            </form>
          </div>
        </div>
      )}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxImage(null)}>&times;</button>
            <img src={lightboxImage.src} alt={lightboxImage.alt} className="lightbox-img" />
            <div className="lightbox-caption">{lightboxImage.caption}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
